import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../../App.css";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { config } from "../../config";
import { useNavigate } from "react-router-dom";

function SearchForm({
  selectedDestination,
  initialOrigin = "",
  initialDestination = "",
  initialDateRange = [new Date(), new Date()],
  initialPassengers = { adults: 1, children: 0, infants: 0, class: "Economy" },
}) {
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState(
    initialDateRange[1] ? "Return" : "One way"
  );
  const [formData, setFormData] = useState({
    origin: initialOrigin,
    destination: selectedDestination || initialDestination,
    dateRange: initialDateRange,
    passengers: initialPassengers,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const cabinClassOptions = [
    { display: "Economy", value: "economy" },
    { display: "Premium Economy", value: "premium_economy" },
    { display: "Business", value: "business" },
    { display: "First", value: "first" },
  ];

  useEffect(() => {
    if (selectedDestination) {
      setFormData((prev) => ({
        ...prev,
        destination: selectedDestination,
      }));
    }
  }, [selectedDestination]);

  useEffect(() => {
    const fetchAirportDetails = async (code) => {
      if (!code) return null;

      try {
        const response = await fetch(
          `${config.backendServer}/api/places?query=${encodeURIComponent(code)}`
        );
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        // Find the exact match for the IATA code
        return data.data.find((airport) => airport.iata_code === code);
      } catch (error) {
        console.error("Error fetching airport details:", error);
        return null;
      }
    };

    const initializeAirports = async () => {
      // Extract IATA codes from initial values if they exist
      const originMatch = initialOrigin.match(/([A-Z]{3})$/);
      const destMatch = initialDestination.match(/([A-Z]{3})$/);

      if (originMatch) {
        const originAirport = await fetchAirportDetails(originMatch[1]);
        if (originAirport) {
          setFormData((prev) => ({
            ...prev,
            origin: `${originAirport.name} (${originAirport.iata_code})`,
          }));
        }
      }

      if (destMatch) {
        const destAirport = await fetchAirportDetails(destMatch[1]);
        if (destAirport) {
          setFormData((prev) => ({
            ...prev,
            destination: `${destAirport.name} (${destAirport.iata_code})`,
          }));
        }
      }
    };

    initializeAirports();
  }, [initialOrigin, initialDestination]);

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    if (option === "One way") {
      setFormData({
        ...formData,
        dateRange: [formData.dateRange[0], null],
      });
    } else {
      // If switching to Return, set return date to next day if not already set
      setFormData({
        ...formData,
        dateRange: [
          formData.dateRange[0],
          formData.dateRange[1] ||
            new Date(formData.dateRange[0].getTime() + 86400000),
        ],
      });
    }
  };

  const handleDateChange = (dates) => {
    if (selectedOption === "One way") {
      // For one-way trips, dates will be a single date
      setFormData({ ...formData, dateRange: [dates, null] });
    } else {
      // For return trips, dates will be an array of [start, end]
      const [start, end] = dates;
      setFormData({ ...formData, dateRange: [start, end] });
    }
  };

  const fetchSuggestions = async (query) => {
    if (!query) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `${config.backendServer}/api/places?query=${encodeURIComponent(query)}`
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      setSuggestions(data.data || []);
    } catch (error) {
      console.error("Error fetching place suggestions:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "origin" || name === "destination") {
      fetchSuggestions(value);
    }
  };

  const handleSearch = () => {
    const [departureDate, returnDate] = formData.dateRange;

    // Extract IATA codes from the selected airport names
    const originMatch = formData.origin.match(/\(([A-Z]{3})\)$/);
    const destinationMatch = formData.destination.match(/\(([A-Z]{3})\)$/);

    if (!originMatch || !destinationMatch || !departureDate) {
      alert("Please fill in all required fields");
      return;
    }

    // Format dates to YYYY-MM-DD
    const formatDate = (date) =>
      date ? date.toISOString().split("T")[0] : null;

    // Find the correct cabin class value
    const cabinClassValue =
      cabinClassOptions.find(
        (option) => option.display === formData.passengers.class
      )?.value || "economy";

    const searchData = {
      origin: originMatch[1],
      destination: destinationMatch[1],
      departure_date: formatDate(departureDate),
      return_date: selectedOption === "Return" ? formatDate(returnDate) : null,
      adults: formData.passengers.adults,
      children: formData.passengers.children || 0,
      infants: formData.passengers.infants || 0,
      cabin_class: cabinClassValue,
    };

    const queryString = new URLSearchParams(
      Object.fromEntries(
        Object.entries(searchData).filter(([_, value]) => value !== null)
      )
    ).toString();

    navigate(`/flights/search?${queryString}`);
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const updatePassengers = (type, action) => {
    setFormData((prev) => {
      const newPassengers = { ...prev.passengers };

      if (type === "adults") {
        // Prevent reducing adults below 1
        if (action === "decrement" && newPassengers.adults <= 1) {
          return prev;
        }
      }

      newPassengers[type] =
        action === "increment"
          ? newPassengers[type] + 1
          : Math.max(0, newPassengers[type] - 1);

      return {
        ...prev,
        passengers: newPassengers,
      };
    });
  };

  const totalPassengers =
    formData.passengers.adults +
    formData.passengers.children +
    formData.passengers.infants;

  const passengerText = `${totalPassengers} Passengers, ${formData.passengers.class}`;

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="w-full max-w-6xl mx-auto bg-black bg-opacity-20 backdrop-blur-sm p-4 md:p-6 rounded-lg">
        <div className="flex justify-center mb-4">
          <div className="bg-black bg-opacity-30 rounded-full p-1">
            <button
              className={`px-4 py-2 text-sm md:text-base ${
                selectedOption === "Return"
                  ? `bg-white ${config.primaryColorBg} rounded-full`
                  : "bg-transparent text-white"
              } rounded-l-full transition-colors`}
              onClick={() => handleOptionClick("Return")}
            >
              Return
            </button>
            <button
              className={`px-4 py-2 text-sm md:text-base ${
                selectedOption === "One way"
                  ? `bg-white ${config.primaryColorBg} rounded-full`
                  : "bg-transparent text-white"
              } transition-colors`}
              onClick={() => handleOptionClick("One way")}
            >
              One way
            </button>
          </div>
        </div>
        <div className="bg-white w-full flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2 p-3 md:p-5 rounded-3xl">
          <input
            type="text"
            name="origin"
            placeholder="Where from?"
            className="flex-1 p-3 rounded-xl md:rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
            onChange={handleInputChange}
            value={formData.origin}
            list="origin-suggestions"
          />
          <datalist id="origin-suggestions">
            {suggestions.map((suggestion) => (
              <option
                key={suggestion.id}
                value={`${suggestion.name} (${suggestion.iata_code})`}
              />
            ))}
          </datalist>
          <input
            type="text"
            name="destination"
            placeholder="Where to?"
            className="flex-1 p-3 rounded-xl md:rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
            value={formData.destination}
            onChange={handleInputChange}
            list="destination-suggestions"
          />
          <datalist id="destination-suggestions">
            {suggestions.map((suggestion) => (
              <option
                key={suggestion.id}
                value={`${suggestion.name} (${suggestion.iata_code})`}
              />
            ))}
          </datalist>
          <DatePicker
            selected={formData.dateRange[0]}
            onChange={handleDateChange}
            startDate={formData.dateRange[0]}
            endDate={selectedOption === "Return" ? formData.dateRange[1] : null}
            selectsRange={selectedOption === "Return"}
            minDate={new Date()}
            className="flex-1 p-3 rounded-xl md:rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 w-full"
            placeholderText={
              selectedOption === "Return" ? "Departure - Return" : "Departure"
            }
          />
          <button
            className="flex-1 p-3 rounded-xl md:rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 truncate overflow-hidden whitespace-nowrap text-left"
            onClick={toggleModal}
          >
            {passengerText}
          </button>
          <button
            className={`h-12 w-full md:w-12 ${config.primaryColorBg} text-white rounded-xl md:rounded-full flex items-center justify-center hover:bg-purple-700 transition-colors`}
            onClick={handleSearch}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </button>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-md p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-left mb-6">Passengers</h3>
            <div className="flex justify-between mb-6">
              <p className="font-light text-gray-500">
                <span className="font-bold text-black">Adults</span> (12+)
              </p>
              <div className="flex items-center">
                <button
                  className="px-2 rounded-full border"
                  onClick={() => updatePassengers("adults", "decrement")}
                >
                  -
                </button>
                <span className="px-2">{formData.passengers.adults}</span>
                <button
                  className="px-2 rounded-full border"
                  onClick={() => updatePassengers("adults", "increment")}
                >
                  +
                </button>
              </div>
            </div>
            <div className="flex justify-between mb-6">
              <p className="font-light text-gray-500">
                <span className="font-bold text-black">Children</span> (2-11)
              </p>
              <div className="flex items-center">
                <button
                  className="px-2 rounded-full border"
                  onClick={() => updatePassengers("children", "decrement")}
                >
                  -
                </button>
                <span className="px-2">{formData.passengers.children}</span>
                <button
                  className="px-2 rounded-full border"
                  onClick={() => updatePassengers("children", "increment")}
                >
                  +
                </button>
              </div>
            </div>
            <div className="flex justify-between mb-6">
              <p className="font-light text-gray-500">
                <span className="font-bold text-black">Infants</span> (0-1)
              </p>
              <div className="flex items-center">
                <button
                  className="px-2 rounded-full border"
                  onClick={() => updatePassengers("infants", "decrement")}
                >
                  -
                </button>
                <span className="px-2">{formData.passengers.infants}</span>
                <button
                  className="px-2 rounded-full border"
                  onClick={() => updatePassengers("infants", "increment")}
                >
                  +
                </button>
              </div>
            </div>
            <div className="mt-4">
              <select
                value={
                  cabinClassOptions.find(
                    (option) => option.display === formData.passengers.class
                  )?.value || "economy"
                }
                onChange={(e) => {
                  const selectedOption = cabinClassOptions.find(
                    (option) => option.value === e.target.value
                  );
                  setFormData({
                    ...formData,
                    passengers: {
                      ...formData.passengers,
                      class: selectedOption.display,
                    },
                  });
                }}
                className="w-full p-2 border rounded"
              >
                {cabinClassOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.display}
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                className={`text-white ${config.primaryColorBg} px-4 py-2 rounded hover:text-white`}
                onClick={toggleModal}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchForm;
